import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Sparkles, Inbox, Headphones, Wand2 } from 'lucide-react';

/**
 * Full-screen success toast shown for 5s after a Custom Quote modal
 * submission. Auto-dismisses via the timer OR by clicking anywhere.
 *
 * Intentionally chromeless (no dialog header / close button) so the
 * reveal feels like a confirmation moment, not another form.
 */
const LINES = [
  { icon: Sparkles, text: 'Our EG3 Product Experts are now crafting your custom solution.' },
  { icon: Inbox, text: 'Delivered straight to your inbox — fast and hassle-free.' },
  { icon: Headphones, text: 'Complimentary expert support included.' },
  { icon: Wand2, text: 'Discover exclusive sizes & materials not offered online.' },
];

const OVERLAY_VARIANTS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};
const CARD_VARIANTS = {
  initial: { opacity: 0, y: 24, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 16, scale: 0.98 },
};
// Hoisted module-scope so Framer-Motion doesn't re-subscribe on every
// render (one of the iter63b code-review findings).
const OVERLAY_TRANSITION = { duration: 0.25 };
const CARD_TRANSITION = { duration: 0.35, ease: [0.22, 1, 0.36, 1] };
const LIST_ITEM_INITIAL = { opacity: 0, x: -8 };
const LIST_ITEM_ANIMATE = { opacity: 1, x: 0 };

export const QuoteSubmittedToast = ({ open, onClose, autoCloseMs = 5000 }) => {
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => onClose?.(), autoCloseMs);
    return () => clearTimeout(t);
  }, [open, onClose, autoCloseMs]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="overlay"
          variants={OVERLAY_VARIANTS}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={OVERLAY_TRANSITION}
          onClick={onClose}
          data-testid="quote-submitted-toast"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0F1D14]/60 backdrop-blur-sm px-4"
        >
          <motion.div
            variants={CARD_VARIANTS}
            transition={CARD_TRANSITION}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-lg w-full bg-white rounded-2xl shadow-2xl ring-1 ring-[#4A7C59]/20 overflow-hidden"
          >
            <div className="bg-gradient-to-br from-[#4A7C59] via-[#2F5D3A] to-[#0F2B18] text-white px-8 pt-8 pb-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-white/15 ring-4 ring-white/10 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-8 h-8 text-[#BDEBB4]" />
              </div>
              <h3 className="text-2xl font-bold tracking-tight">
                Your Personalized Quote Has Been Submitted
              </h3>
              <div className="mt-2 h-1 w-16 mx-auto rounded-full bg-[#6FBF73]/60" />
            </div>
            <ul className="px-8 py-5 space-y-3">
              {LINES.map(({ icon: Icon, text }, i) => (
                <motion.li
                  key={text}
                  initial={LIST_ITEM_INITIAL}
                  animate={LIST_ITEM_ANIMATE}
                  transition={{ delay: 0.15 + i * 0.07 }}
                  className="flex items-start gap-3 text-sm text-[#2F5D3A]"
                >
                  <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-[#E6F4E8] flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#4A7C59]" />
                  </span>
                  <span className="leading-snug">{text}</span>
                </motion.li>
              ))}
            </ul>
            <div className="px-8 pb-5 text-center text-xs text-gray-400">
              This message will disappear on its own.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuoteSubmittedToast;
